/**
 * CommandPalette Component
 *
 * A global search dialog triggered by Ctrl+K / Cmd+K.
 * Searches patients and provides quick-action navigation.
 * Uses MUI components with subtle backdrop blur.
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Dialog,
  TextField,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Box,
  Chip,
  InputAdornment,
  Divider,
} from '@mui/material';
import {
  Search as SearchIcon,
  Person as PersonIcon,
  PersonAdd as PersonAddIcon,
  EventNote as SessionsIcon,
  CalendarMonth as CalendarIcon,
  Dashboard as DashboardIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useRealtimeDataRead } from '../../contexts/RealtimeDataContext';

interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
}

interface CommandItem {
  id: string;
  label: string;
  category: 'patients' | 'actions';
  icon: React.ReactNode;
  action: () => void;
}

const CommandPalette: React.FC<CommandPaletteProps> = ({ open, onClose }) => {
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const navigate = useNavigate();
  const { patients } = useRealtimeDataRead();
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [debouncedQuery, setDebouncedQuery] = useState('');

  // Debounce the search query by 200ms
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      setDebouncedQuery(query);
    }, 200);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [query]);

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setQuery('');
      setDebouncedQuery('');
      setSelectedIndex(0);
      // Focus the input after the dialog opens
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [open]);

  // Build static quick actions
  const quickActions: CommandItem[] = useMemo(() => [
    {
      id: 'action-new-patient',
      label: 'Novo Paciente',
      category: 'actions' as const,
      icon: <PersonAddIcon fontSize="small" />,
      action: () => navigate('/patients/new'),
    },
    {
      id: 'action-new-session',
      label: 'Nova Sessão',
      category: 'actions' as const,
      icon: <SessionsIcon fontSize="small" />,
      action: () => navigate('/sessions'),
    },
    {
      id: 'action-schedule',
      label: 'Agendar Consulta',
      category: 'actions' as const,
      icon: <CalendarIcon fontSize="small" />,
      action: () => navigate('/calendar'),
    },
    {
      id: 'action-dashboard',
      label: 'Ver Dashboard',
      category: 'actions' as const,
      icon: <DashboardIcon fontSize="small" />,
      action: () => navigate('/dashboard'),
    },
    {
      id: 'action-export',
      label: 'Exportar Dados',
      category: 'actions' as const,
      icon: <SettingsIcon fontSize="small" />,
      action: () => navigate('/settings'),
    },
  ], [navigate]);

  // Filter patients and actions based on debounced query
  const filteredResults = useMemo(() => {
    const results: CommandItem[] = [];
    const search = debouncedQuery.toLowerCase().trim();

    // Filter patients
    const activePatients = (patients || []).filter((p) => !p.deleted);
    const matchedPatients = search
      // Defensivo: p.name pode ser null em paciente importado
      ? activePatients.filter((p) => (p.name || '').toLowerCase().includes(search))
      : activePatients;
    const limitedPatients = matchedPatients.slice(0, 5);

    limitedPatients.forEach((patient) => {
      results.push({
        id: `patient-${patient.id}`,
        label: patient.name,
        category: 'patients',
        icon: <PersonIcon fontSize="small" />,
        action: () => navigate(`/sessions/${patient.id}`),
      });
    });

    // Filter actions
    const matchedActions = search
      ? quickActions.filter((a) => a.label.toLowerCase().includes(search))
      : quickActions;

    results.push(...matchedActions);

    return results;
  }, [debouncedQuery, patients, quickActions, navigate]);

  // Reset selected index when results change
  useEffect(() => {
    setSelectedIndex(0);
  }, [filteredResults.length]);

  // Scroll the selected item into view
  useEffect(() => {
    if (listRef.current) {
      const selectedEl = listRef.current.querySelector(`[data-index="${selectedIndex}"]`);
      if (selectedEl) {
        selectedEl.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [selectedIndex]);

  const handleSelect = useCallback(
    (item: CommandItem) => {
      onClose();
      item.action();
    },
    [onClose]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex((prev) =>
          prev < filteredResults.length - 1 ? prev + 1 : 0
        );
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex((prev) =>
          prev > 0 ? prev - 1 : filteredResults.length - 1
        );
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (filteredResults[selectedIndex]) {
          handleSelect(filteredResults[selectedIndex]);
        }
      } else if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    },
    [filteredResults, selectedIndex, handleSelect, onClose]
  );

  // Group results by category for rendering with section headers
  const patientResults = filteredResults.filter((r) => r.category === 'patients');
  const actionResults = filteredResults.filter((r) => r.category === 'actions');

  // Build flat list with section awareness for keyboard navigation
  const getGlobalIndex = (category: 'patients' | 'actions', localIndex: number): number => {
    if (category === 'patients') return localIndex;
    return patientResults.length + localIndex;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          position: 'fixed',
          top: '15%',
          m: 0,
          borderRadius: 2,
          maxHeight: '70vh',
          overflow: 'hidden',
        },
      }}
      BackdropProps={{
        sx: {
          backdropFilter: 'blur(4px)',
          backgroundColor: 'rgba(0, 0, 0, 0.3)',
        },
      }}
      // Prevent the default Dialog escape handling so our keyDown handler manages it
      disableEscapeKeyDown={false}
    >
      <Box sx={{ p: 0 }}>
        {/* Search input */}
        <Box sx={{ p: 2, pb: 1 }}>
          <TextField
            inputRef={inputRef}
            fullWidth
            placeholder="Buscar pacientes, ações..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            variant="outlined"
            size="medium"
            autoComplete="off"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <Chip
                    label="ESC"
                    size="small"
                    variant="outlined"
                    sx={{ fontSize: '0.7rem', height: 22 }}
                  />
                </InputAdornment>
              ),
              sx: {
                borderRadius: 2,
              },
            }}
          />
        </Box>

        {/* Results */}
        <Box sx={{ maxHeight: '55vh', overflowY: 'auto', pb: 1 }}>
          {filteredResults.length === 0 ? (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Nenhum resultado encontrado
              </Typography>
            </Box>
          ) : (
            <List ref={listRef} dense disablePadding>
              {/* Patients section */}
              {patientResults.length > 0 && (
                <>
                  <Box sx={{ px: 2, pt: 1.5, pb: 0.5 }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}
                    >
                      Pacientes
                    </Typography>
                  </Box>
                  {patientResults.map((item, localIndex) => {
                    const globalIdx = getGlobalIndex('patients', localIndex);
                    return (
                      <ListItem
                        key={item.id}
                        data-index={globalIdx}
                        disablePadding
                        onClick={() => handleSelect(item)}
                        sx={{
                          cursor: 'pointer',
                          mx: 1,
                          borderRadius: 1,
                          bgcolor:
                            selectedIndex === globalIdx
                              ? 'action.hover'
                              : 'transparent',
                          '&:hover': {
                            bgcolor: 'action.hover',
                          },
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 40, pl: 1 }}>
                          {item.icon}
                        </ListItemIcon>
                        <ListItemText
                          primary={item.label}
                          primaryTypographyProps={{ variant: 'body2' }}
                        />
                        <Chip
                          label="Paciente"
                          size="small"
                          variant="outlined"
                          color="primary"
                          sx={{ fontSize: '0.65rem', height: 20, mr: 1 }}
                        />
                      </ListItem>
                    );
                  })}
                </>
              )}

              {/* Divider between sections */}
              {patientResults.length > 0 && actionResults.length > 0 && (
                <Divider sx={{ my: 1, mx: 2 }} />
              )}

              {/* Actions section */}
              {actionResults.length > 0 && (
                <>
                  <Box sx={{ px: 2, pt: 1.5, pb: 0.5 }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}
                    >
                      Ações Rápidas
                    </Typography>
                  </Box>
                  {actionResults.map((item, localIndex) => {
                    const globalIdx = getGlobalIndex('actions', localIndex);
                    return (
                      <ListItem
                        key={item.id}
                        data-index={globalIdx}
                        disablePadding
                        onClick={() => handleSelect(item)}
                        sx={{
                          cursor: 'pointer',
                          mx: 1,
                          borderRadius: 1,
                          bgcolor:
                            selectedIndex === globalIdx
                              ? 'action.hover'
                              : 'transparent',
                          '&:hover': {
                            bgcolor: 'action.hover',
                          },
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 40, pl: 1 }}>
                          {item.icon}
                        </ListItemIcon>
                        <ListItemText
                          primary={item.label}
                          primaryTypographyProps={{ variant: 'body2' }}
                        />
                        <Chip
                          label="Acao"
                          size="small"
                          variant="outlined"
                          color="secondary"
                          sx={{ fontSize: '0.65rem', height: 20, mr: 1 }}
                        />
                      </ListItem>
                    );
                  })}
                </>
              )}
            </List>
          )}
        </Box>

        {/* Footer hint */}
        <Box
          sx={{
            px: 2,
            py: 1,
            borderTop: 1,
            borderColor: 'divider',
            display: 'flex',
            gap: 2,
            alignItems: 'center',
          }}
        >
          <Typography variant="caption" color="text.secondary">
            <Box component="span" sx={{ fontFamily: 'monospace', bgcolor: 'action.hover', px: 0.5, borderRadius: 0.5 }}>
              ↑↓
            </Box>
            {' '}navegar
          </Typography>
          <Typography variant="caption" color="text.secondary">
            <Box component="span" sx={{ fontFamily: 'monospace', bgcolor: 'action.hover', px: 0.5, borderRadius: 0.5 }}>
              Enter
            </Box>
            {' '}selecionar
          </Typography>
          <Typography variant="caption" color="text.secondary">
            <Box component="span" sx={{ fontFamily: 'monospace', bgcolor: 'action.hover', px: 0.5, borderRadius: 0.5 }}>
              Esc
            </Box>
            {' '}fechar
          </Typography>
        </Box>
      </Box>
    </Dialog>
  );
};

export default CommandPalette;
